/**
 * vocab.ts — Etichette e ordinamento canonico di Periodo / Movimento / Genere.
 *
 * Le unioni chiuse a livello di TIPO vivono in schema.ts (così il compilatore
 * intercetta i refusi nei JSON degli autori). Qui stanno le etichette leggibili
 * (IT + EN) e l'ordine con cui i filtri le mostrano.
 *
 * Aggiungere un valore: va messo SIA nell'unione in schema.ts SIA qui. Il
 * layer di rendering legge esclusivamente da questo file.
 */

import type { Period, Movement, Genre, ThemeId } from './schema';

export type UiLocale = 'it' | 'en';

export interface VocabEntry<T extends string> {
  id: T;
  label_it: string;
  label_en: string;
  order: number;
}

// ══════ PERIODI (cronologici) ══════

export const PERIOD_VOCAB: ReadonlyArray<VocabEntry<Period>> = [
  { id: 'origini-duecento',       label_it: 'Origini e Duecento',        label_en: 'Origins & 13th c.',        order: 1 },
  { id: 'trecento',               label_it: 'Trecento',                  label_en: '14th century',             order: 2 },
  { id: 'umanesimo-rinascimento', label_it: 'Umanesimo e Rinascimento',  label_en: 'Humanism & Renaissance',   order: 3 },
  { id: 'seicento-barocco',       label_it: 'Seicento e Barocco',        label_en: '17th c. & Baroque',        order: 4 },
  { id: 'settecento-illuminismo', label_it: 'Settecento e Illuminismo',  label_en: '18th c. & Enlightenment',  order: 5 },
  { id: 'primo-ottocento',        label_it: 'Primo Ottocento',           label_en: 'Early 19th century',       order: 6 },
  { id: 'secondo-ottocento',      label_it: 'Secondo Ottocento',         label_en: 'Late 19th century',        order: 7 },
  { id: 'primo-novecento',        label_it: 'Primo Novecento',           label_en: 'Early 20th century',       order: 8 },
  { id: 'secondo-novecento',      label_it: 'Secondo Novecento',         label_en: 'Late 20th century',        order: 9 },
];

// ══════ MOVIMENTI ══════

export const MOVEMENT_VOCAB: ReadonlyArray<VocabEntry<Movement>> = [
  { id: 'scuola-siciliana', label_it: 'Scuola siciliana',  label_en: 'Sicilian School',     order: 1 },
  { id: 'dolce-stil-novo',  label_it: 'Dolce Stil Novo',   label_en: 'Dolce Stil Novo',     order: 2 },
  { id: 'umanesimo',        label_it: 'Umanesimo',         label_en: 'Humanism',            order: 3 },
  { id: 'rinascimento',     label_it: 'Rinascimento',      label_en: 'Renaissance',         order: 4 },
  { id: 'petrarchismo',     label_it: 'Petrarchismo',      label_en: 'Petrarchism',         order: 5 },
  { id: 'controriforma',    label_it: 'Controriforma',     label_en: 'Counter-Reformation', order: 6 },
  { id: 'barocco',          label_it: 'Barocco',           label_en: 'Baroque',             order: 7 },
  { id: 'arcadia',          label_it: 'Arcadia',           label_en: 'Arcadia',             order: 8 },
  { id: 'illuminismo',      label_it: 'Illuminismo',       label_en: 'Enlightenment',       order: 9 },
  { id: 'neoclassicismo',   label_it: 'Neoclassicismo',    label_en: 'Neoclassicism',       order: 10 },
  { id: 'romanticismo',     label_it: 'Romanticismo',      label_en: 'Romanticism',         order: 11 },
  { id: 'verismo',          label_it: 'Verismo',           label_en: 'Verismo',             order: 12 },
  { id: 'decadentismo',     label_it: 'Decadentismo',      label_en: 'Decadentism',         order: 13 },
  { id: 'futurismo',        label_it: 'Futurismo',         label_en: 'Futurism',            order: 14 },
  { id: 'crepuscolarismo',  label_it: 'Crepuscolarismo',   label_en: 'Crepuscolarismo',     order: 15 },
  { id: 'ermetismo',        label_it: 'Ermetismo',         label_en: 'Hermeticism',         order: 16 },
  { id: 'neorealismo',      label_it: 'Neorealismo',       label_en: 'Neorealism',          order: 17 },
  { id: 'postmoderno',      label_it: 'Postmoderno',       label_en: 'Postmodernism',       order: 18 },
];

// ══════ GENERI ══════

export const GENRE_VOCAB: ReadonlyArray<VocabEntry<Genre>> = [
  { id: 'lirica',              label_it: 'Lirica',                 label_en: 'Lyric poetry',       order: 1 },
  { id: 'sonetto',             label_it: 'Sonetto',                label_en: 'Sonnet',             order: 2 },
  { id: 'canzone',             label_it: 'Canzone',                label_en: 'Canzone',            order: 3 },
  { id: 'idillio',             label_it: 'Idillio',                label_en: 'Idyll',              order: 4 },
  { id: 'canto',               label_it: 'Canto',                  label_en: 'Canto',              order: 5 },
  { id: 'poesia-versi-liberi', label_it: 'Poesia in versi liberi', label_en: 'Free verse',         order: 6 },
  { id: 'poema-cavalleresco',  label_it: 'Poema cavalleresco',     label_en: 'Chivalric romance',  order: 7 },
  { id: 'poema-eroico',        label_it: 'Poema eroico',           label_en: 'Heroic poem',        order: 8 },
  { id: 'novella',             label_it: 'Novella',                label_en: 'Novella',            order: 9 },
  { id: 'racconto',            label_it: 'Racconto',               label_en: 'Short story',        order: 10 },
  { id: 'romanzo',             label_it: 'Romanzo',                label_en: 'Novel',              order: 11 },
  { id: 'romanzo-storico',     label_it: 'Romanzo storico',        label_en: 'Historical novel',   order: 12 },
  { id: 'romanzo-epistolare',  label_it: 'Romanzo epistolare',     label_en: 'Epistolary novel',   order: 13 },
  { id: 'commedia',            label_it: 'Commedia',               label_en: 'Comedy',             order: 14 },
  { id: 'tragedia',            label_it: 'Tragedia',               label_en: 'Tragedy',            order: 15 },
  { id: 'dialogo',             label_it: 'Dialogo',                label_en: 'Dialogue',           order: 16 },
  { id: 'trattato',            label_it: 'Trattato',               label_en: 'Treatise',           order: 17 },
  { id: 'saggio',              label_it: 'Saggio',                 label_en: 'Essay',              order: 18 },
  { id: 'prosa-scientifica',   label_it: 'Prosa scientifica',      label_en: 'Scientific prose',   order: 19 },
  { id: 'memoriale',           label_it: 'Memoriale',              label_en: 'Memoir',             order: 20 },
];

// ══════ LOOKUP ══════

const byId = <T extends string>(v: ReadonlyArray<VocabEntry<T>>) =>
  new Map(v.map((e) => [e.id as string, e]));

const PERIOD_BY_ID = byId(PERIOD_VOCAB);
const MOVEMENT_BY_ID = byId(MOVEMENT_VOCAB);
const GENRE_BY_ID = byId(GENRE_VOCAB);

/** Etichette dei tipi di nodo nelle mappe (condivise col golden). */
const NODE_KIND_LABELS: Record<string, { it: string; en: string }> = {
  work:     { it: 'Opera',     en: 'Work' },
  movement: { it: 'Movimento', en: 'Movement' },
  genre:    { it: 'Genere',    en: 'Genre' },
  theme:    { it: 'Tema',      en: 'Theme' },
  unknown:  { it: 'Tema',      en: 'Theme' },
};

const label = <T extends string>(e: VocabEntry<T> | undefined, id: string, locale: UiLocale) =>
  e ? (locale === 'en' ? e.label_en : e.label_it) : id;

/** Etichetta di un tema (movimento o genere) nella lingua richiesta. */
export function themeLabel(id: ThemeId, locale: UiLocale = 'it'): string {
  return label(MOVEMENT_BY_ID.get(id) ?? GENRE_BY_ID.get(id), id, locale);
}

export function periodLabel(id: Period, locale: UiLocale = 'it'): string {
  return label(PERIOD_BY_ID.get(id), id, locale);
}

export function genreLabel(id: Genre, locale: UiLocale = 'it'): string {
  return label(GENRE_BY_ID.get(id), id, locale);
}

export function movementLabel(id: Movement, locale: UiLocale = 'it'): string {
  return label(MOVEMENT_BY_ID.get(id), id, locale);
}

/** Un tema è un movimento o un genere: serve alle mappe per scegliere la lente. */
export function themeKind(id: ThemeId): 'movement' | 'genre' | 'unknown' {
  if (MOVEMENT_BY_ID.has(id)) return 'movement';
  if (GENRE_BY_ID.has(id)) return 'genre';
  return 'unknown';
}

export function nodeKindLabel(kind: string, locale: UiLocale = 'it'): string {
  const e = NODE_KIND_LABELS[kind];
  return e ? (locale === 'en' ? e.en : e.it) : kind;
}
